/**
 * <okendo-reviews> - a one-review-at-a-time Okendo review strip.
 *
 * The rating, review count and "read all" link are rendered server-side in
 * snippets/pvl-okendo-reviews.liquid from the okendo.summaryData metafield, so
 * they are correct before this script runs and stay correct if it never does.
 * The only thing fetched here is the rotating quote.
 *
 * Pure helpers live in assets/pvl-okendo-data.js (window.PVLOkendoData) so
 * scripts/pvl-okendo-reviews.test.js can exercise them under Node.
 */

const SWIPE_THRESHOLD_PX = 30;

// Okendo hands review bodies that arrived through the Shop app
// (externalProvider "shopify-shop") back HTML-escaped ("peach &amp; grape")
// and its own raw ("ice & mixes"), so a straight textContent assignment prints
// the entities literally. A <textarea> parses its innerHTML as RCDATA: entities
// decode, "<" never opens a tag and nothing executes, so it is the one decoder
// that is also safe on the raw bodies. DOMParser would eat "x <y" as a tag.
const decoder = document.createElement("textarea");
function decodeEntities(text) {
  decoder.innerHTML = text || "";
  return decoder.value;
}

class OkendoReviews extends HTMLElement {
  connectedCallback() {
    this.data = window.PVLOkendoData;
    if (!this.data) return;

    // The theme editor disconnects and reconnects this same instance when a
    // block moves, so every piece of load state resets here rather than in a
    // constructor that only runs once.
    this.reviews = [];
    this.index = 0;
    this.autoplayStopped = false;
    this.timer = null;
    this.controller = new AbortController();

    this.config = this.readConfig();
    this.refs = {
      quote: this.querySelector("[data-okendo-quote]"),
      name: this.querySelector("[data-okendo-name]"),
      verified: this.querySelector("[data-okendo-verified]"),
      flavour: this.querySelector("[data-okendo-flavour]"),
      dots: this.querySelector("[data-okendo-dots]"),
      viewport: this.querySelector("[data-okendo-viewport]"),
    };

    // Every listener rides the same AbortController as the fetch, so
    // disconnectedCallback tears all of it down in one abort() and nothing
    // depends on holding a matching function reference. Required by
    // .cursor/rules/carousel-accessibility.mdc, and the reason is that
    // removeEventListener with a freshly .bind()-ed handler never matches the
    // reference addEventListener stored, so the listener is never removed and
    // the theme editor leaks a fresh set on every block edit.
    const signal = this.controller.signal;

    if (this.refs.dots) {
      this.refs.dots.addEventListener("click", (event) => this.onDotClick(event), { signal });
    }
    if (this.refs.viewport) {
      this.refs.viewport.addEventListener("touchstart", (event) => this.onTouchStart(event), { passive: true, signal });
      this.refs.viewport.addEventListener("touchend", (event) => this.onTouchEnd(event), { passive: true, signal });
    }
    this.addEventListener("pointerenter", () => this.pauseAutoplay(), { signal });
    this.addEventListener("pointerleave", () => this.resumeAutoplay(), { signal });
    this.addEventListener("focusin", () => this.pauseAutoplay(), { signal });
    this.addEventListener("focusout", () => this.resumeAutoplay(), { signal });
    document.addEventListener("visibilitychange", () => this.onVisibility(), { signal });

    this.load();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    // Aborts the in-flight fetch and removes every listener registered above.
    if (this.controller) this.controller.abort();
  }

  readConfig() {
    const node = this.querySelector("[data-okendo-config]");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  text(name, replacements) {
    let value = this.dataset[name] || "";
    Object.keys(replacements || {}).forEach(function (key) {
      value = value.replace(key, replacements[key]);
    });
    return value;
  }

  async load() {
    try {
      await this.loadReviews();
    } catch (error) {
      // allSettled already absorbs a failing request, so reaching here means
      // something structural went wrong - a malformed config, a response that
      // is not JSON. Either way the shopper gets told rather than left with an
      // empty strip and the reason buried in the console.
      if (this.controller.signal.aborted) return;
      console.warn("[okendo-reviews] could not load reviews", error);
      this.showMessage(this.text("textLoadError"));
    }
  }

  async loadReviews() {
    const { subscriberId, productIds, reviewsToLoad } = this.config;
    if (!subscriberId || !Array.isArray(productIds) || productIds.length === 0) {
      this.showMessage(this.text("textNoReviews"));
      return;
    }

    // One request per source product, in parallel. Sources are this product
    // plus its Combined Listings siblings, so this is one or a handful - and
    // allSettled, not all, so a single failing sibling still leaves the
    // others' reviews on the page.
    const settled = await Promise.allSettled(
      productIds.map((productId) =>
        fetch(
          // orderBy matters: only the first page is read, so without it the
          // API's default order can hand back the oldest reviews and sorting
          // three old reviews still leaves three old reviews on the page.
          this.data.productEndpoint(subscriberId, productId, "reviews", {
            limit: String(reviewsToLoad || 3),
            orderBy: "date desc",
          }),
          { signal: this.controller.signal },
        ).then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        }),
      ),
    );

    if (this.controller.signal.aborted) return;

    const pages = settled
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value);

    if (pages.length === 0) {
      this.showMessage(this.text("textLoadError"));
      return;
    }

    const merged = pages.reduce(
      (all, page) => all.concat((page && page.reviews) || []),
      [],
    );
    this.reviews = this.data.pickReviews(merged, reviewsToLoad || 3);

    if (this.reviews.length === 0) {
      this.showMessage(this.text("textNoReviews"));
      return;
    }

    this.buildDots();
    this.show(0);
    this.startAutoplay();
    // Covers the case where autoplay never started at all - a 0 setting, a
    // single review, or reduced motion - and the region should be polite.
    this.syncLiveRegion();
  }

  showMessage(message) {
    if (this.refs.quote) {
      this.refs.quote.textContent = message;
      // "No reviews yet." is not a quotation, so the decorative curly quotes
      // the stylesheet adds are suppressed for it.
      this.refs.quote.classList.add("pvl-okendo__quote--message");
    }
    if (this.refs.name) this.refs.name.textContent = "";
    if (this.refs.verified) this.refs.verified.hidden = true;
    if (this.refs.flavour) this.refs.flavour.textContent = "";
    if (this.refs.dots) this.refs.dots.hidden = true;
  }

  buildDots() {
    const dots = this.refs.dots;
    if (!dots) return;

    // Cleared first: the theme editor can re-run load() on this same instance.
    dots.textContent = "";
    dots.hidden = this.reviews.length < 2;

    this.reviews.forEach((review, index) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "pvl-okendo__dot";
      dot.dataset.okendoIndex = String(index);
      dot.setAttribute(
        "aria-label",
        this.text("textGoToReview", {
          __INDEX__: String(index + 1),
          __TOTAL__: String(this.reviews.length),
        }),
      );
      dots.appendChild(dot);
    });
  }

  show(index) {
    const review = this.reviews[index];
    if (!review) return;
    this.index = index;

    if (this.refs.quote) this.refs.quote.classList.remove("pvl-okendo__quote--message");

    // review.body and displayName are shopper-submitted. textContent, never
    // innerHTML - decodeEntities only turns "&amp;" back into "&".
    if (this.refs.quote) this.refs.quote.textContent = decodeEntities(review.body);
    if (this.refs.name) {
      this.refs.name.textContent = decodeEntities(
        review.reviewer && review.reviewer.displayName,
      );
    }
    if (this.refs.verified) {
      this.refs.verified.hidden = !(review.reviewer && review.reviewer.isVerified);
    }
    if (this.refs.flavour) {
      this.refs.flavour.textContent = decodeEntities(
        this.data.flavourOf(review, this.config.variantFlavours),
      );
    }

    if (this.refs.dots) {
      Array.from(this.refs.dots.children).forEach((dot, dotIndex) => {
        dot.classList.toggle("pvl-okendo__dot--active", dotIndex === index);
        // aria-current, not aria-selected: these are buttons, not tabs.
        if (dotIndex === index) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    }
  }

  step(delta) {
    this.show(this.data.wrapIndex(this.index, delta, this.reviews.length));
  }

  onDotClick(event) {
    const dot = event.target.closest("[data-okendo-index]");
    if (!dot) return;
    // A deliberate choice means the shopper is driving now: WCAG 2.2.2 wants a
    // mechanism to stop auto-updating content, and these keyboard-reachable
    // dots are it.
    this.stopAutoplay();
    this.autoplayStopped = true;
    this.show(Number(dot.dataset.okendoIndex));
  }

  onTouchStart(event) {
    this.touchStartX = event.touches[0] ? event.touches[0].clientX : null;
    this.touchStartY = event.touches[0] ? event.touches[0].clientY : null;
  }

  onTouchEnd(event) {
    const startX = this.touchStartX;
    const startY = this.touchStartY;
    this.touchStartX = null;
    this.touchStartY = null;
    if (startX == null || startY == null) return;

    const touch = event.changedTouches[0];
    const endX = touch ? touch.clientX : null;
    const endY = touch ? touch.clientY : null;
    if (endX == null || endY == null) return;

    const delta = endX - startX;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    // A finger flicking the page vertically rarely travels straight, so a long
    // scroll drifts far enough sideways to clear the threshold above. Without
    // this the scroll counts as a deliberate swipe: it advances the review the
    // shopper was reading and stops rotation for good, since autoplayStopped
    // is one-way. Horizontal movement must dominate for it to be a swipe.
    if (Math.abs(endY - startY) >= Math.abs(delta)) return;

    this.stopAutoplay();
    this.autoplayStopped = true;
    this.step(delta < 0 ? 1 : -1);
  }

  onVisibility() {
    if (document.hidden) {
      this.pauseAutoplay();
    } else {
      this.resumeAutoplay();
    }
  }

  /**
   * off while rotating, polite once it is not.
   *
   * .cursor/rules/carousel-accessibility.mdc asks for both states: announcing
   * every automatic change would talk over a screen reader continuously, while
   * staying off after the shopper takes over would leave their own dot
   * activation silent.
   */
  syncLiveRegion() {
    if (!this.refs.viewport) return;
    this.refs.viewport.setAttribute("aria-live", this.timer ? "off" : "polite");
  }

  startAutoplay() {
    const configured = Number(this.config.autoplaySeconds || 0);
    if (configured <= 0 || this.autoplayStopped) return;
    // WCAG 2.2.2 and .cursor/rules/carousel-accessibility.mdc both put the
    // floor at five seconds, so a shorter setting is raised rather than
    // honoured. The block's help text says so.
    const seconds = Math.max(5, configured);
    if (this.reviews.length < 2) return;
    // Rotation is motion nobody asked for, so reduced-motion users never get
    // it and the strip stays on its first review until they act.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.stopAutoplay();
    this.timer = window.setInterval(() => this.step(1), seconds * 1000);
    this.syncLiveRegion();
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.syncLiveRegion();
  }

  pauseAutoplay() {
    this.stopAutoplay();
  }

  resumeAutoplay() {
    if (!this.autoplayStopped && !document.hidden) this.startAutoplay();
  }
}

if (!customElements.get("okendo-reviews")) {
  customElements.define("okendo-reviews", OkendoReviews);
}
